import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  CardHeader,
  Badge,
  Button,
  Table
} from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { getUnitById } from "../../../slices/thunks";
import UnitTimeline from "./UnitTimeline";
import moment from "moment";

// Component for displaying info in a table
const InfoTable = ({ data }) => (
  <Table className="custom-table">
    <tbody>
      {data.map((item, index) => (
        <tr key={index}>
          <th style={{ paddingLeft: 0, width: "40%" }}>{item.label}</th>
          <td className="text-muted">{item.value}</td>
        </tr>
      ))}
    </tbody>
  </Table>
);

// Component for section headers with icons
const SectionHeader = ({ icon, title, color }) => (
  <div className="d-flex align-items-center">
    <span
      className="text-white rounded-circle p-2 me-2 shadow-sm"
      style={{ backgroundColor: color }}
    >
      <i className={`${icon} fs-5`}></i>
    </span>
    <h5 className="mb-0">{title}</h5>
  </div>
);

// Component for small card headers with icons
const SmallCardHeader = ({ icon, title, color }) => (
  <div className="d-flex align-items-center">
    <span
      className="rounded-circle p-1 me-2"
      style={{ 
        backgroundColor: `rgba(${color}, 0.1)`, 
        width: "28px", 
        height: "28px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <i className={`${icon} fs-6`} style={{ color: `rgb(${color})` }}></i>
    </span>
    <h6 className="mb-0">{title}</h6>
  </div>
);

// Component for financial detail rows
const FinancialRow = ({ label, value, isHighlighted = false, isCurrency = true }) => (
  <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
    <h6 className="mb-0">{label}</h6>
    <div className={`fs-5 fw-medium ${isHighlighted ? (value > 0 ? 'text-success' : 'text-danger') : ''}`}>
      {isCurrency && '€'}
      {isCurrency 
        ? new Intl.NumberFormat("en-US").format(value || 0)
        : value}
      {isHighlighted && value > 0 && '+'}
    </div>
  </div>
);

// Loading component
const LoadingState = ({ color }) => (
  <div className="page-content">
    <Container fluid>
      <div className="text-center mt-5">
        <div className="spinner-grow" style={{ color, width: "3rem", height: "3rem" }} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3 fs-5">Loading unit details...</p>
      </div>
    </Container>
  </div>
);

// Error component
const ErrorState = ({ error, color, onRetry }) => (
  <div className="page-content">
    <Container fluid>
      <div className="text-center mt-5">
        <div className="alert alert-danger p-4 shadow-sm border-0" style={{ borderRadius: "12px" }}>
          <i className="ri-error-warning-line fs-1 d-block mb-3"></i>
          <h4 className="mb-3">Failed to load unit details</h4>
          <p className="mb-4">{error}</p>
          <Button
            style={{ 
              backgroundColor: color, 
              borderColor: color, 
              padding: "10px 24px",
              borderRadius: "8px" 
            }}
            onClick={onRetry}
          >
            <i className="ri-refresh-line me-1"></i> Retry
          </Button>
        </div>
      </div>
    </Container>
  </div>
);

// Main card component
const StyledCard = ({ children, className = "" }) => (
  <Card
    className={`${className} border-0 shadow-lg card-hover`}
    style={{ borderRadius: "12px", transition: "all 0.3s ease" }}
  >
    {children}
  </Card>
);

const UnitDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [unit, setUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Colors
  const colors = {
    primary: "#4b38b3",
    success: "#0ab39c",
    warning: "#f7b84b",
    info: "#299cdb",
    primaryRgb: "75, 56, 179",
    successRgb: "10, 179, 156" 
  };

  const fetchUnitData = useCallback(() => {
    if (id) {
      setLoading(true);
      dispatch(getUnitById(id))
        .unwrap()
        .then((data) => {
          setUnit(data);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Failed to fetch unit details:", error);
          setError(error.message || "Failed to load unit details");
          setLoading(false);
        });
    }
  }, [id, dispatch]);

  useEffect(() => {
    fetchUnitData();
  }, [fetchUnitData]);

  const formatDate = (date) => {
    return date ? moment(date).format("DD MMM YYYY") : "N/A";
  };

  if (loading) {
    return <LoadingState color={colors.primary} />;
  }

  if (error) {
    return <ErrorState error={error} color={colors.primary} onRetry={() => window.location.reload()} />;
  }

  // Calculate financial metrics
  const pricePerSqm = unit?.net_sqm ? Math.round(unit.settled_price / unit.net_sqm) : 0;
  const priceDifference = (unit?.settled_price || 0) - (unit?.initial_price || 0);
  const totalArea = (unit?.gross_sqm || 0) + (unit?.terrace_net_sqm || 0);

  // Prepare data for basic information table
  const basicInfoData = [
    { label: "Unit Type", value: unit?.unit_type || "N/A" },
    { label: "Apt Type", value: unit?.apt_type || "N/A" },
    { label: "Floor", value: unit?.floor || "N/A" },
    { label: "Block", value: unit?.block || "N/A" },
    { label: "ID", value: unit?.id || "N/A" },
    { label: "Created", value: formatDate(unit?.created_at) }
  ];

  // Prepare data for area details table
  const areaInfoData = [
    { label: "Net Area", value: `${unit?.net_sqm || 0} sqm` },
    { label: "Gross Area", value: `${unit?.gross_sqm || 0} sqm` },
    { label: "Terrace Area", value: `${unit?.terrace_net_sqm || 0} sqm` },
    { label: "Rooms", value: unit?.num_rooms || 0 },
    { label: "Price/sqm", value: `€${new Intl.NumberFormat("en-US").format(pricePerSqm)}` },
    { label: "Total Area", value: `${totalArea} sqm` }
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb pageTitle="Units" />

        <Row className="g-4">
          <Col lg={8}>
            {/* Main Details */}
            <StyledCard className="mb-4">
              <CardHeader className="bg-transparent border-bottom">
                <SectionHeader 
                  icon="ri-information-line" 
                  title="Unit Information" 
                  color={colors.primary} 
                />
              </CardHeader>
              <CardBody className="p-4">
                <Row>
                  {/* Basic Information */}
                  <Col lg={6}>
                    <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "10px" }}>
                      <div className="card-header bg-transparent border-bottom" style={{ borderRadius: "10px 10px 0 0" }}>
                        <SmallCardHeader 
                          icon="ri-building-line" 
                          title="Basic Information" 
                          color={colors.primaryRgb} 
                        />
                      </div>
                      <div className="card-body">
                        <InfoTable data={basicInfoData} />
                      </div>
                    </div>
                  </Col>

                  {/* Area Details */}
                  <Col lg={6}>
                    <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "10px" }}>
                      <div className="card-header bg-transparent border-bottom" style={{ borderRadius: "10px 10px 0 0" }}>
                        <SmallCardHeader 
                          icon="ri-expand-line" 
                          title="Area Details" 
                          color={colors.successRgb} 
                        />
                      </div>
                      <div className="card-body">
                        <InfoTable data={areaInfoData} />
                      </div>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </StyledCard>

            {/* Unit Timeline */}
            <StyledCard>
              <CardHeader className="bg-transparent border-bottom">
                <SectionHeader 
                  icon="ri-history-line" 
                  title="Unit Timeline" 
                  color={colors.warning} 
                />
              </CardHeader>
              <CardBody className="p-4">
                {unit && <UnitTimeline unit={unit} />}
              </CardBody>
            </StyledCard>
          </Col>

          <Col lg={4}>
            {/* Financial Details */}
            <StyledCard className="mb-4">
              <CardHeader className="bg-transparent border-bottom">
                <SectionHeader 
                  icon="ri-money-euro-circle-line" 
                  title="Financial Details" 
                  color={colors.success} 
                />
              </CardHeader>
              <CardBody className="p-4">
                <div className="financial-summary">
                  <FinancialRow label="Initial Price" value={unit?.initial_price || 0} />
                  <FinancialRow label="Settled Price" value={unit?.settled_price || 0} />
                  <FinancialRow label="Price per sqm" value={pricePerSqm} />
                  
                  {unit?.settled_price !== unit?.initial_price && (
                    <FinancialRow 
                      label="Price Difference" 
                      value={priceDifference} 
                      isHighlighted={true} 
                    />
                  )}
                  
                  <div className="alert alert-light mb-0 d-flex align-items-center" style={{ borderRadius: "8px" }}>
                    <i className="ri-information-line me-2 fs-5 text-muted"></i>
                    <small className="text-muted">Financial details are based on the most recent contract information.</small>
                  </div>
                </div>
              </CardBody>
            </StyledCard>
            
            {/* Actions Card */}
            {/* <StyledCard>
              <CardHeader className="bg-transparent border-bottom">
                <SectionHeader 
                  icon="ri-settings-line" 
                  title="Actions" 
                  color={colors.info} 
                />
              </CardHeader>
              <CardBody className="p-4">
                <div className="d-grid gap-3">
                  <Button
                    color="primary"
                    className="w-100 py-2 d-flex align-items-center justify-content-center"
                    style={{ 
                      backgroundColor: colors.primary, 
                      borderColor: colors.primary,
                      borderRadius: "8px"
                    }}
                  >
                    <i className="ri-edit-line me-1"></i> Edit Unit Details
                  </Button>
                  
                  {unit?.contract ? (
                    <Button
                      color="info"
                      className="w-100 py-2 d-flex align-items-center justify-content-center"
                      style={{ borderRadius: "8px" }}
                      onClick={() => navigate(`/contract-details/${unit.contract}`)}
                    >
                      <i className="ri-file-list-line me-1"></i> View Contract
                    </Button>
                  ) : (
                    <Button
                      color="success"
                      className="w-100 py-2 d-flex align-items-center justify-content-center"
                      style={{ borderRadius: "8px" }}
                    >
                      <i className="ri-add-line me-1"></i> Create Contract
                    </Button>
                  )}
                  
                  <Button
                    color="light"
                    className="w-100 py-2 d-flex align-items-center justify-content-center"
                    style={{ borderRadius: "8px" }}
                  >
                    <i className="ri-printer-line me-1"></i> Print Details
                  </Button>
                </div>
              </CardBody>
            </StyledCard> */}
          </Col>
        </Row>
      </Container>
      
      <style>
        {`
          .text-purple {
            color: ${colors.primary} !important;
          }
          
          .card-hover:hover {
            transform: translateY(-5px);
            box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.175) !important;
          }
          
          .glass-card {
            background-color: rgba(255, 255, 255, 0.15);
            backdrop-filter: blur(10px);
            border-radius: 12px;
            border: 1px solid rgba(255, 255, 255, 0.2);
          }
          
          .header-card {
            position: relative;
            overflow: hidden;
          }
          
          .header-card::before {
            content: "";
            position: absolute;
            top: -50%;
            left: -50%;
            width: 200%;
            height: 200%;
            background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 60%);
            transform: rotate(30deg);
          }
          
          .text-white-75 {
            color: rgba(255, 255, 255, 0.75) !important;
          }
          
          .fs-7 {
            font-size: 0.875rem !important;
          }
          
          .custom-table {
            margin-bottom: 0;
          }
          
          .custom-table th, .custom-table td {
            padding: 0.75rem 0;
            border-color: #f0f0f0;
          }
          
          .custom-table tr:last-child th, .custom-table tr:last-child td {
            border-bottom: none;
          }
          
          .rounded-circle {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
          }
          
          .financial-summary > div {
            padding-bottom: 1rem;
          }
          
          .financial-summary > div:last-child {
            border-bottom: 0;
            padding-bottom: 0;
            margin-bottom: 0;
          }
        `}
      </style>
    </div>
  );
};

export default UnitDetail;